import type { FC } from 'react';
import { Box, Typography } from '@mui/material';
import { CommonSelect, type SelectProps } from 'components/common';
import { Locale } from '../../../../constants';

interface SelectFacebookPageAttrs {
  multiples: boolean;
  placeholder: string;
}

type SelectLanguageProps = Partial<Pick<SelectProps, 'handleChange' | 'name' | 'inputLabel'> & SelectFacebookPageAttrs>;

const LANGUAGE_NAMES: Record<string, string> = Object.fromEntries(
  Object.entries(Locale).map(([key, value]) => [
    value,
    key
      .split('_')
      .map((word) => `${word.charAt(0)}${word.slice(1).toLowerCase()}`)
      .join(' '),
  ]),
);

// Swedish is stored as `sv` but operators also refer to it by the country code `SE`.
const getLanguageCode = (value: string) =>
  value === String(Locale.SWEDISH) ? 'SV / SE' : value.toUpperCase();

const SelectLanguage: FC<SelectLanguageProps> =
  ({
     handleChange,
     name,
     inputLabel,
     multiples,
     placeholder,
   }) => {
    // Label carries both code and name so the autocomplete filter matches either.
    const options = Object.entries(Locale).map(([, value]) => ({
      label: `${getLanguageCode(value)} ${LANGUAGE_NAMES[value]}`,
      value: value,
    }));

    return (
      <CommonSelect
        name={name ?? 'language'}
        inputLabel={inputLabel ?? 'Language'}
        options={options}
        handleChange={handleChange}
        placeholder={placeholder}
        multiples={multiples}
        asAutocomplete
        renderOption={(option) => (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', gap: 2 }}>
            <span>{getLanguageCode(String(option.value))}</span>
            <Typography variant="body2" color="text.secondary">
              {LANGUAGE_NAMES[String(option.value)]}
            </Typography>
          </Box>
        )}
      />
    );

  };

export default SelectLanguage;
